/**
 * Add-Row (Acuity) draft → occupancy writes, merge-aware (OR-1152).
 *
 * The backend keeps ONE occupancy record per (facility, year) whose four
 * bed-count columns are the Acuity care "Types". A user-added draft row
 * (facility × Type, census per year) therefore must NOT blindly POST a new
 * record per year: when the facility already has a record for that year the
 * draft's Type value belongs to a column of the EXISTING record.
 *
 * The old flow POSTed every year unconditionally, so a duplicate (facility,
 * year) either 409'd mid-loop — leaving the earlier years persisted (the
 * "partial write" QA saw) — or, on environments where the DB constraint
 * isn't applied, silently inserted duplicate rows.
 */
import type { OccupancyCreateWire, OccupancyUpdateWire } from "../../../api";
import type { OccupancyListItemWire } from "../../../api/endpoints/occupancies";
import type { AcuityBedField } from "./types";

/** The subset of a NewAcuityDraft this module needs (keeps it page-agnostic). */
export interface AcuityDraftFields {
    facilityId: string;
    type: AcuityBedField | "";
    /** year → input string. */
    values: Record<string, string>;
}

export interface AcuityOccupancyPatch {
    occupancyId: string;
    payload: OccupancyUpdateWire;
}

export interface AcuityDraftWrites {
    /** Years whose (facility, year) record already exists → PATCH its column. */
    patches: AcuityOccupancyPatch[];
    /** Years with no record yet → POST a new one. */
    creates: OccupancyCreateWire[];
}

function toIntOrNull(v: string): number | null {
    const trimmed = v.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed.replace(/,/g, ""));
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** Index key for a (facility, year) pair — exported so the save flow can
 *  register records it just created (two drafts in one save may target the
 *  same facility+year; the second must PATCH, not re-POST). */
export function occupancyKey(facilityId: string, year: number | string): string {
    return `${facilityId}|${year}`;
}

/** Index the loaded occupancy list by (facility, year) → record id, so a
 *  draft can be resolved into patches vs creates. Rows without facility_id
 *  can't be matched by a draft (drafts always carry a facility) — skipped. */
export function occupancyIdByFacilityYear(items: OccupancyListItemWire[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const item of items) {
        if (!item.facility_id) continue;
        map.set(occupancyKey(item.facility_id, item.year), item.id);
    }
    return map;
}

/** Resolve a draft into merge-aware writes: for each filled year, PATCH the
 *  existing (facility, year) record's selected bed column, or CREATE the
 *  record when none exists. Empty when the draft is incomplete. */
export function draftToOccupancyWrites(draft: AcuityDraftFields, existing: Map<string, string>): AcuityDraftWrites {
    const writes: AcuityDraftWrites = { patches: [], creates: [] };
    if (!draft.facilityId || !draft.type) return writes;
    for (const [year, raw] of Object.entries(draft.values)) {
        const n = toIntOrNull(raw);
        if (n === null) continue;
        const occupancyId = existing.get(occupancyKey(draft.facilityId, year));
        if (occupancyId) {
            writes.patches.push({ occupancyId, payload: { [draft.type]: n } as OccupancyUpdateWire });
        } else {
            writes.creates.push({ facility_id: draft.facilityId, year: Number(year), [draft.type]: n } as OccupancyCreateWire);
        }
    }
    return writes;
}

/** Backend errors speak HTTP/DB — the Acuity tab shouldn't. Translate the
 *  known cases to friendly copy; pass anything else through.
 *  - 409 duplicate (facility, year) → point the user at the existing row.
 *  - 422 validation (e.g. negative/invalid bed counts) → the inputs are
 *    sanitized to positive integers (OR-1416), but if an invalid payload
 *    still reaches submit, show a clear message instead of "Request failed: 422". */
export function friendlyAcuitySaveError(message: string): string {
    const dup = /occupancy row for year (\d{4}) already exists/i.exec(message);
    if (dup) {
        return `An Acuity record for year ${dup[1]} already exists for this facility — edit that year's value on the existing row instead.`;
    }
    if (/\b422\b|unprocessable/i.test(message)) {
        return "Bed counts must be positive whole numbers.";
    }
    return message;
}
